import React, { Suspense, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import type { GalleryItem } from '../types/gallery';
import Layout from '../components/Layout';
import ProtectedRoute from '../components/ProtectedRoute';
import AdminRoute from '../components/AdminRoute';
import OrdersStaffRoute from '../components/OrdersStaffRoute';
import LoginPage from '../pages/LoginPage';
import RegisterCompanyPage from '../pages/RegisterCompanyPage';
import OrganizationPage from '../pages/OrganizationPage';
import HomePage from '../components/HomePage';
import GalleryPage from '../components/GalleryPage';
import Editor from '../components/Editor';
import MoodboardsPage from '../components/MoodboardsPage';
import ProfilePage from '../components/ProfilePage';
import HelpPage from '../components/HelpPage';
import TermsPage from '../components/TermsPage';
import PrivacyPage from '../components/PrivacyPage';
import UserTickets from '../pages/UserTickets';
import AdminCompaniesPage from '../pages/admin/AdminCompaniesPage';
import AdminCompanyDetailPage from '../pages/admin/AdminCompanyDetailPage';

import CustomerDetailPage from '../components/admin/CustomerDetailPage';
import QuickEditModal from '../components/shared/QuickEditModal';
import AuthModal from '../components/shared/AuthModal';
import FeedbackModal from '../components/shared/FeedbackModal';
import ModuleGate from '../components/shared/ModuleGate';
import MarketingSegmentRoute from '../components/marketing-segment/MarketingSegmentRoute';

// DEV-ONLY: Design System showcase — tree-shaken from production/staging builds.
// import.meta.env.DEV is a literal `false` in `vite build`, so Vite's dead-code
// elimination drops the dynamic import AND the route path string "/__ds-preview"
// — neither the showcase chunk nor the route literal is emitted in the prod bundle.
const DsPreviewPage = import.meta.env.DEV
  ? React.lazy(() =>
      import('../pages/DsPreviewPage').then((m) => ({ default: m.DsPreviewPage }))
    )
  : null;

// Lazy-loaded heavy pages for better initial bundle size
const BatchCreation = React.lazy(() => import('../components/BatchCreation'));
const HomeDecor = React.lazy(() => import('../components/HomeDecor'));
const Moodboard = React.lazy(() => import('../components/Moodboard'));
const BrandbookPage = React.lazy(() => import('../components/BrandbookPage'));
const Admin = React.lazy(() => import('../pages/Admin'));
const StillsVestuarioPage = React.lazy(() => import('../pages/StillsVestuarioPage'));
const StillsProdutosPage = React.lazy(() => import('../pages/StillsProdutosPage'));
const EyewearPage = React.lazy(() => import('../pages/EyewearPage'));
const JewelryPage = React.lazy(() => import('../pages/JewelryPage'));
const VeoStudioPage = React.lazy(() => import('../pages/VeoStudioPage'));
const ModelCreatorPage = React.lazy(() => import('../pages/ModelCreatorPage'));
const MyModelsPage = React.lazy(() => import('../pages/MyModelsPage'));
const MarketingStudioPage = React.lazy(() => import('../pages/MarketingStudioPage'));
const AdminMarketingTemplatesPage = React.lazy(() => import('../pages/admin/AdminMarketingTemplatesPage'));
const ConnectorsModePage = React.lazy(() => import('../pages/ConnectorsModePage'));
const FacadesPage = React.lazy(() => import('../pages/FacadesPage'));
const SantaLollaPage = React.lazy(() => import('../pages/SantaLollaPage'));
const TrendForgeWizardPage = React.lazy(() => import('../pages/trendforge/TrendForgeWizardPage'));
const VTOAttributionPage = React.lazy(() => import('../pages/admin/VTOAttributionPage'));
const VTOSessionsPage = React.lazy(() => import('../pages/admin/VTOSessionsPage'));
const MyOrdersPage = React.lazy(() => import('../pages/MyOrdersPage'));
const OrderDeliveryPage = React.lazy(() => import('../pages/OrderDeliveryPage'));
const AdminClientsPage = React.lazy(() => import('../pages/admin/AdminClientsPage'));
const AdminKanbanPage = React.lazy(() => import('../pages/admin/AdminKanbanPage'));
const AdminOrderPage = React.lazy(() => import('../pages/admin/AdminOrderPage'));
// Segmento Marketing (seletor Fashion | Marketing na barra superior).
const MarketingHomePage = React.lazy(() => import('../pages/marketing/MarketingHomePage'));
const BrandDnaPage = React.lazy(() => import('../pages/marketing/BrandDnaPage'));
const NewBrandPage = React.lazy(() => import('../pages/marketing/NewBrandPage'));
const ProductsPage = React.lazy(() => import('../pages/marketing/ProductsPage'));
const ImportProductsPage = React.lazy(() => import('../pages/marketing/ImportProductsPage'));
const ImageAdPage = React.lazy(() => import('../pages/marketing/ImageAdPage'));
const ImageAdBuilderPage = React.lazy(() => import('../pages/marketing/ImageAdBuilderPage'));
const ImageAdDetailPage = React.lazy(() => import('../pages/marketing/ImageAdDetailPage'));
const AdsLibraryPage = React.lazy(() => import('../pages/marketing/AdsLibraryPage'));

const LazyFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7063f7]" />
  </div>
);

import { useAuthStore } from '../stores/useAuthStore';
import { isSnapfyInternal } from '../utils/snapfyInternal';
import { useGalleryStore } from '../stores/useGalleryStore';
import { useGenerationStore } from '../stores/useGenerationStore';
import { useFeedbackModalStore } from '../stores/useFeedbackModalStore';
import { useHomeGeneration } from '../hooks/useHomeGeneration';
import { getPendingGeneration, clearPendingGeneration, restorePendingFile } from '../hooks/usePendingGeneration';

const AppRouter: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();
  const {
    items: userGallery,
    brandbooks,
    fetchBrandbooks,
    addItem: handleItemGeneratedForBatch,
    updateItem,
    addBrandbook: handleAddBrandbook,
  } = useGalleryStore();
  const {
    homePageResult,
    isLoading,
    error,
    prompt,
    editingImage,
    editingImageOrderContext,
    setPrompt,
    setEditingImage,
    setHomePageResult,
  } = useGenerationStore();
  const { handleGenerate, handleSelectStyle, handleDownload } = useHomeGeneration();
  const navigate = useNavigate();

  // Video creation is unified on the /video page: instead of opening a modal, send
  // the chosen image to /video preloaded as the start image (image-to-video mode).
  // Resolve a backendId so /video can load the image via the proxy (avoids S3 CORS):
  // some sources only carry `id` ("img_<backendId>"), not a separate backendId field.
  const handleStartVideoCreation = useCallback((item: GalleryItem | null) => {
    if (!item || item.type !== 'image') return;
    const backendId = item.backendId != null
      ? Number(item.backendId)
      : (/^img_\d+$/.test(item.id) ? Number(item.id.replace('img_', '')) : undefined);
    navigate('/video', { state: { startImage: { src: item.src, backendId, type: 'image' } } });
  }, [navigate]);
  const feedbackIsOpen = useFeedbackModalStore((state) => state.isOpen);
  const feedbackCloseModal = useFeedbackModalStore((state) => state.closeModal);

  // Restore pending generation after login
  const pendingGenerationProcessed = useRef(false);
  useEffect(() => {
    if (isAuthenticated && !pendingGenerationProcessed.current) {
      const pending = getPendingGeneration();
      if (pending) {
        pendingGenerationProcessed.current = true;
        clearPendingGeneration();
        // Restore the file and trigger generation
        restorePendingFile(pending).then((file) => {
          if (file || pending.prompt) {
            setPrompt(pending.prompt);
            // Small delay to ensure state is updated
            setTimeout(() => {
              handleGenerate(file ? [file as File] : [], pending.prompt, pending.styleDetails, pending.aspectRatio);
            }, 500);
          }
        });
      }
    }
  }, [isAuthenticated, handleGenerate, setPrompt]);

  // Fetch brandbooks on mount. A galeria NAO e pre-carregada aqui: cada
  // consumidor de items busca sob demanda (GalleryPage faz o proprio fetch
  // paginado/filtrado; GallerySourceNode chama fetchGallery se items vazio).
  // Evita 1 GET /images (full-res, sem thumbnail) em toda sessao do app.
  useEffect(() => {
    fetchBrandbooks();
  }, [fetchBrandbooks]);

  // Handler for batch item updates — maps (id, newSrc) to updateItem
  const handleItemUpdatedForBatch = (id: string, newSrc: string) => {
    updateItem(id, { src: newSrc } as any);
  };

  return (
    <>
      <Suspense fallback={<LazyFallback />}>
      <Routes>
        {/* DEV-ONLY showcase — public, unauthenticated, tree-shaken from prod/staging bundle */}
        {import.meta.env.DEV && DsPreviewPage && (
          <Route path="/__ds-preview" element={<DsPreviewPage />} />
        )}
        {/*
          Ambiente interno: a unica porta publica e o /login.
          Cadastro, recuperacao de senha, convite de empresa, billing e suporte
          foram retirados de proposito — nao ha criacao de conta aqui (as contas
          vieram do clone da staging), nao ha envio de e-mail (MAIL_MAILER=log) e
          nao ha cliente pagante. Qualquer outro caminho cai no /login pelo
          catch-all mais abaixo, entao um visitante anonimo nao ve tela nenhuma
          alem da de entrada.
        */}
        <Route path="/login" element={<LoginPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<Layout />}>
            <Route path="/" element={
              <HomePage
                error={error}
                isLoading={isLoading}
                homePageResult={homePageResult}
                prompt={prompt}
                onPromptChange={setPrompt}
                onGenerate={handleGenerate}
                onSelectStyle={handleSelectStyle}
                onDownload={handleDownload}
                onEditImage={setEditingImage}
                onStartVideoCreation={handleStartVideoCreation}
              />
            } />
            <Route path="/batch" element={
              <BatchCreation
                brandbooks={brandbooks}
                userGallery={userGallery}
                userEmail={user?.email}
                onItemGenerated={handleItemGeneratedForBatch}
                onItemUpdated={handleItemUpdatedForBatch}
                onEditItem={setEditingImage}
                onStartVideoCreation={handleStartVideoCreation}
                onRefreshBrandbooks={fetchBrandbooks}
              />
            } />
            <Route path="/homedecor" element={
              <HomeDecor
                brandbooks={brandbooks}
                userGallery={userGallery}
                onItemGenerated={handleItemGeneratedForBatch}
                onEditItem={setEditingImage}
                onStartVideoCreation={handleStartVideoCreation}
              />
            } />
            <Route path="/editor" element={<Editor />} />
            <Route path="/gallery" element={
              <GalleryPage items={userGallery} onEditItem={setEditingImage} onStartVideoCreation={handleStartVideoCreation} />
            } />
            <Route path="/moodboards" element={<MoodboardsPage />} />
            {/* Fachadas: app novo, ainda nao validado com cliente. A rota responde
                apenas para contas @snapfy.ai — esconder so o item do menu deixaria a
                URL acessivel a quem a digitasse. Abrir e trocar por <FacadesPage />. */}
            <Route path="/facades" element={isSnapfyInternal(user) ? <FacadesPage /> : <Navigate to="/" replace />} />
            {/* Santa Lolla: app interno de troca de calçado por SKU (só @snapfy.ai, como Fachadas). */}
            <Route path="/santa-lolla" element={isSnapfyInternal(user) ? <SantaLollaPage /> : <Navigate to="/" replace />} />
            <Route path="/moodboard" element={
              <Moodboard
                onAddBrandbook={handleAddBrandbook}
                onItemGenerated={handleItemGeneratedForBatch}
                onRefreshBrandbooks={fetchBrandbooks}
              />
            } />
            <Route path="/brandbook" element={
              <BrandbookPage brandbooks={brandbooks} onAddBrandbook={handleAddBrandbook} />
            } />
            {/* Operação de Pedidos: equipe @snapfy.ai verificada, não só admin. */}
            <Route element={<OrdersStaffRoute />}>
              <Route path="/admin/clients" element={<AdminClientsPage />} />
              <Route path="/admin/kanban" element={<AdminKanbanPage />} />
              <Route path="/admin/orders/:id" element={<AdminOrderPage />} />
            </Route>

            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/companies" element={<AdminCompaniesPage />} />
              <Route path="/admin/companies/:id" element={<AdminCompanyDetailPage />} />
              <Route path="/admin/vto-attribution" element={<VTOAttributionPage />} />
              <Route path="/admin/vto-sessions" element={<VTOSessionsPage />} />

              <Route path="/admin/marketing-templates" element={<AdminMarketingTemplatesPage />} />

              <Route path="/admin/billing/customer/:userId" element={<CustomerDetailPage />} />
            </Route>
            <Route path="/orders" element={<MyOrdersPage />} />
            <Route path="/orders/:id/delivery" element={<OrderDeliveryPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/tickets" element={<UserTickets />} />
            <Route path="/stills/vestuario" element={<ModuleGate slug="stills_vestuario"><StillsVestuarioPage /></ModuleGate>} />
            <Route path="/stills/produtos" element={<ModuleGate slug="stills_produtos"><StillsProdutosPage /></ModuleGate>} />
            <Route path="/accessories/eyewear" element={<ModuleGate slug="eyewear"><EyewearPage /></ModuleGate>} />
            <Route path="/accessories/jewelry" element={<ModuleGate slug="jewelry"><JewelryPage /></ModuleGate>} />
            <Route path="/video" element={<ModuleGate slug="veo_studio"><VeoStudioPage /></ModuleGate>} />
            {/* Legacy path kept so existing links/bookmarks don't 404. Preserve
                query params (UTM/referral) when redirecting. */}
            <Route path="/veo" element={<Navigate to={{ pathname: '/video', search: window.location.search }} replace />} />
            <Route path="/models" element={<ModuleGate slug="my_models"><MyModelsPage /></ModuleGate>} />
            <Route path="/models/create" element={<ModuleGate slug="model_creator"><ModelCreatorPage /></ModuleGate>} />
            <Route path="/workflow" element={<ModuleGate slug="workflow"><ConnectorsModePage /></ModuleGate>} />
            <Route path="/marketing-studio" element={<ModuleGate slug="marketing_studio"><MarketingStudioPage /></ModuleGate>} />
            <Route path="/trendforge" element={<ModuleGate slug="trendforge"><TrendForgeWizardPage /></ModuleGate>} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/register-company" element={<RegisterCompanyPage />} />
            <Route path="/organization" element={<OrganizationPage />} />
            {/* Segmento Marketing: tudo sob /marketing usa o menu da plataforma Marketing.
                Playground e Animar imagem reaproveitam o Canvas e o Veo Studio. */}
            <Route element={<MarketingSegmentRoute />}>
              <Route path="/marketing" element={<MarketingHomePage />} />
              <Route path="/marketing/playground" element={
                <Moodboard
                  onAddBrandbook={handleAddBrandbook}
                  onItemGenerated={handleItemGeneratedForBatch}
                  onRefreshBrandbooks={fetchBrandbooks}
                />
              } />
              <Route path="/marketing/animar-imagem" element={<ModuleGate slug="veo_studio"><VeoStudioPage /></ModuleGate>} />
              {/* DNA da marca: leitura do site do cliente por IA (app próprio, não é o Brandbook). */}
              <Route path="/marketing/dna" element={<BrandDnaPage />} />
              <Route path="/marketing/dna/novo" element={<NewBrandPage />} />
              {/* Produtos: catálogo da loja da marca + fila "Revisar". */}
              <Route path="/marketing/produtos" element={<ProductsPage />} />
              <Route path="/marketing/produtos/importar" element={<ImportProductsPage />} />
              {/* Anúncio de imagem: galeria de templates com caixas de edição (Seedream <bbox>). */}
              <Route path="/marketing/anuncio-imagem" element={<ImageAdPage />} />
              <Route path="/marketing/anuncio-imagem/novo" element={<ImageAdBuilderPage />} />
              <Route path="/marketing/anuncio-imagem/criativo/:id" element={<ImageAdDetailPage />} />
              <Route path="/marketing/anuncios" element={<AdsLibraryPage />} />
              <Route path="/marketing/*" element={<Navigate to="/marketing" replace />} />
            </Route>
          </Route>
        </Route>
        {/*
          Ambiente interno: nada de 404 informativo para quem nao esta logado.
          Qualquer caminho desconhecido cai no /login — inclusive as rotas
          publicas que foram removidas acima, para que um link antigo nao
          revele que elas existiram.
        */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </Suspense>

      <QuickEditModal
        isOpen={!!editingImage && editingImage.type === 'image'}
        onClose={() => setEditingImage(null)}
        baseImage={editingImage}
        orderEdit={editingImageOrderContext ?? undefined}
        onImageEdited={(newImageUrl, newBackendId) => {
          if (editingImage) {
            // Update master gallery
            updateItem(editingImage.id, { src: newImageUrl, backendId: newBackendId || editingImage.backendId } as any);
            // Update home page result if it's the one being edited
            setHomePageResult(
              homePageResult?.id === editingImage.id && homePageResult.type === 'image'
                ? { ...homePageResult, src: newImageUrl, backendId: newBackendId || homePageResult.backendId }
                : homePageResult
            );
          }
        }}
      />
      <AuthModal />
      <FeedbackModal isOpen={feedbackIsOpen} onClose={feedbackCloseModal} />
    </>
  );
};

export default AppRouter;
